import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {UserData} from "types/User";
import {FormikHelpers} from "formik";
import AudienceMemberForm from '../Form';
import type { AxiosError } from 'axios';

function MutateAudienceMember() {
  const { id } = useParams();
  const { data: audienceMember, isLoading } = useQuery(
    ['users', { id }],
    () => ngage.users.get(Number(id)),
    { enabled: !!id }
  );

  const store = useMutation((data: UserData) => ngage.users.store(data));
  const update = useMutation((data: UserData) => ngage.users.update(parseInt(id as string), data));

  const { notify } = useNotifications();
  const navigate = useNavigate();

  // Handle form submission
  async function handleSubmit(values: UserData, { setSubmitting }: FormikHelpers<UserData>) {
    id ? await handleUpdate(values) : await handleStore(values);
    await invalidate('users.audience');

    setSubmitting(false);
    navigate('/audience');
  }

  // Handle store mutation
  async function handleStore(values: UserData) {
    try {
      await store.mutateAsync(values);
      notify('success', 'Audience Member Registered', `${values.name} has been created`);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      if (axiosError?.response?.data?.message === 'The email has already been taken.') {
        notify(
          'error',
          'Audience Member Not Registered',
          `${values.name} could not be updated because the email is already taken.`,
          5000
        );
      } else {
        notify('error', 'Audience Member Not Registered', `${values.name} could not be stored`);
      }

      throw error;
    }
  }

  // Handle update mutation
  async function handleUpdate(values: UserData) {
    try {
      await update.mutateAsync(values);
      notify('success', 'Audience Member Updated', `${values.name} has been updated`);
      await invalidate(['users', { id }]);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      if (axiosError?.response?.data?.message === 'The email has already been taken.') {
        notify(
          'error',
          'Audience Member Not Updated',
          `${values.name} could not be updated because the email is already taken.`,
          5000
        );
      } else {
        notify('error', 'Audience Member Not Updated', `${values.name} could not be updated`);
      }

      throw error;
    }
  }

  // Prepare breadcrumbs
  const breadcrumbs = {
    Engagement: '/',
    Audience: '/audience',
    [`${id ? 'Edit' : 'New'} Audience Member`]: id ? `/audience/${id}/edit` : '/audience/new',
  };

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} Audience Member`}
      breadcrumbs={breadcrumbs}
      isLoading={isLoading}
    >
      <AudienceMemberForm audienceMember={audienceMember} onSubmit={handleSubmit} />
    </Page>
  );
}

export default MutateAudienceMember;
